import keys from '@debbie/common/dist/cache/keys.js'
import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js'
import helpers from '@debbie/common/dist/economy/helpers/index.js'
import { VoucherCategory } from '@debbie/common/dist/economy/vouchers/index.js'
import { isUUID } from '@debbie/common/dist/imports/data/helpers.js'
import {
  Address,
  BankAccountType,
  Entity,
  PermissionLevels,
  validateEmail,
  validatePhone,
} from '@debbie/common/dist/index.js'
import {
  Direction,
  InteractionType,
} from '@debbie/common/dist/interactions/index.js'
import { Lang } from '@debbie/common/dist/lang/Lang.js'
import * as customersAPI from '@debbie/cortex/dist/api/customers.js'
import { useCaseVouchersByCase } from '@debbie/cortex/dist/case-vouchers/index.js'
import { ensureCaseLoaded, useCase } from '@debbie/cortex/dist/cases/index.js'
import { useCollector } from '@debbie/cortex/dist/collectors/index.js'
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js'
import {
  customersEntity,
  ensureCustomerLoaded,
  useCustomer,
  usePendingCustomerOverpayments,
} from '@debbie/cortex/dist/customers/index.js'
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js'
import { c, h } from '@debbie/cortex/dist/index.js'
import { useMe, useRole } from '@debbie/cortex/dist/me/index.js'
import q from '@debbie/cortex/dist/query.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import {
  joinCaseWatchRoom,
  leaveCaseWatchRoom,
} from '@debbie/cortex/dist/socket.js'
import { useTenant } from '@debbie/cortex/dist/tenant/index.js'
import BankAccount from '@debbie/iris/dist/components/economy/BankAccount.js'
import CaseVouchers from '@debbie/iris/dist/components/economy/case-vouchers/CaseVouchers.js'
import { Col, Container, Row } from '@debbie/iris/dist/components/Grid/index.js'
import CaseHistory from '@debbie/iris/dist/components/Hist/CaseHistory.js'
import Mail from '@debbie/iris/dist/components/Icons/Mail.js'
import Phone from '@debbie/iris/dist/components/Icons/Phone.js'
import ThumbsUp from '@debbie/iris/dist/components/Icons/ThumbsUp.js'
import {
  Button,
  Content,
  Input,
  Money,
  PageBase,
  Panel,
  Pay,
  PlaceholderLoader,
} from '@debbie/iris/dist/components/index.js'
import { NoteInteraction } from '@debbie/iris/dist/components/interactions/index.js'
import LanguageInput from '@debbie/iris/dist/components/LanguageInput.js'
import THEME, { updateTheme } from '@debbie/iris/dist/theme.js'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'

import DebtorInfo from '../../components/DebtorInfo.js'
import Documentation from '../../components/Documentation.js'
import { APIBASE } from '../../config.js'
import {
  AccountInput,
  Footer,
  Header,
  Info,
  Intro,
  OnlyMobile,
  OnlyNotMobile,
} from './CaseStyles.js'

interface Props {
  paid?: boolean
}

interface ProfileData {
  status: 'not-loaded' | 'loading' | 'loaded'
  name: string | null | undefined
  email: string | null | undefined
  phone: string | null | undefined
  address: Address | null | undefined
}

const initialProfile: Omit<ProfileData, 'status'> = {
  name: null,
  email: null,
  phone: null,
  address: null,
}

const Case = (props: Props) => {
  const params = useParams<{ id: string }>()
  const id = params.id

  const caseIdLoader = useCase(id)
  const caseId = h.hasData(caseIdLoader) ? caseIdLoader.data.caseId : null
  const singleCaseLoader = useCase(caseId)
  const singleCase = h.hasData(singleCaseLoader) ? singleCaseLoader.data : null
  const caseError = h.isError(singleCaseLoader) ? singleCaseLoader.error : null
  const lang = useSelector((s: RootState) => s.lang)
  const currentLang = useSelector((s: RootState) => s.currentLang)
  const meLoadable = useMe()
  const me = h.hasData(meLoadable) ? meLoadable.data : null
  const meRole = useRole()

  const caseVouchers_ = useCaseVouchersByCase(singleCase?.caseId)
  const caseVouchers = h.hasData(caseVouchers_) ? caseVouchers_.data : null

  const tenant = useTenant()
  const creditorLoadable = useCreditor(singleCase?.creditorId)
  const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null
  const customerLoader = useCustomer(singleCase?.customerId)
  const customer = h.hasData(customerLoader) ? customerLoader.data : null
  const pendingOverpayments = usePendingCustomerOverpayments(
    customer?.customerId,
  )

  const collectorLoadable = useCollector(
    (singleCase &&
      creditor &&
      getCollectorOnCase(
        singleCase.type,
        singleCase.collectorId,
        creditor.identityCollectorId,
        creditor.defaultCollectorId,
      )) ||
      undefined,
  )

  const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null

  const [previousId, setPreviousId] = useState<null | number | string>(null)
  const [editingBankAccount, setEditingBankAccount] = useState<boolean>(false)
  const [accountNumber, setAccountNumber] = useState<[string, string]>(['', ''])
  const [profileData, setProfileData] = useState<ProfileData>({
    ...initialProfile,
    status: 'not-loaded',
  })
  const [draftEmail, setDraftEmail] = useState<string>('')
  const [emailErrors, setEmailErrors] = useState<string[] | null>(null)

  useEffect(() => {
    if (previousId) {
      leaveCaseWatchRoom(`${previousId}`)
      setPreviousId(id)
    }

    const timestamp = Date.now()
    joinCaseWatchRoom(id, timestamp)

    return () => leaveCaseWatchRoom(id)
  }, [id])

  useEffect(() => {
    if (singleCase && customer) {
      getProfileData()
    }
  }, [singleCase, customer])

  useEffect(() => {
    if (singleCase && isUUID(id)) {
      c.redirect.redirect(`/cases/${singleCase.id}`, { replace: true })
    }
  }, [singleCase])

  const documentsLoadable = useDocumentsByReference(singleCase?.caseId)
  const documents = h.hasData(documentsLoadable) ? documentsLoadable.data : null

  useEffect(() => {
    if (collector) {
      updateTheme(collector.theme)
    }
  }, [collector])

  const getProfileData = () => {
    const debtorUser =
      me && customer && customer.users.find((user) => user.userId === me.id)

    if (debtorUser) {
      setProfileData({
        status: 'loaded',
        email: debtorUser.details.email,
        name: debtorUser.details.name,
        phone: debtorUser.details.phone?.number,
        address: debtorUser.details.address,
      })
    } else if (profileData.status !== 'loading') {
      setProfileData({
        ...profileData,
        status: 'loading',
      })
    }
  }

  // The debtor must have an email so we can reply to their message. When they
  // have none yet, we collect it inline in the composer and persist it as part
  // of sending (instead of disabling the whole message field).
  const needsEmail = profileData.status === 'loaded' && !profileData.email

  const saveDebtorEmailIfNeeded = async (): Promise<boolean> => {
    if (!needsEmail) {
      return true
    }
    if (!validateEmail(draftEmail)) {
      setEmailErrors([lang.customers.errorEmailValidation])
      return false
    }
    if (!me || !customer || !singleCase) {
      return false
    }
    try {
      await customersAPI.requestInformationChange(
        customer.customerId,
        me.id,
        singleCase.caseId,
        { email: draftEmail },
      )
      setProfileData((prev) => ({ ...prev, email: draftEmail }))
      q.invalidate(keys.write.modify(customersEntity, customer.customerId))
      return true
    } catch (error) {
      setEmailErrors([lang.customers.errorEmailValidation])
      return false
    }
  }

  const onSubmit = (e: React.FormEvent, verify: boolean = false) => {
    e.stopPropagation()
    e.preventDefault()

    if (!customer || !singleCase) {
      return
    }

    const changed =
      customer.bankAccount?.accountNumber !== accountNumber[1] ||
      customer.bankAccount?.registrationNumber !== accountNumber[0]

    customersAPI
      .updateBankAccount(
        singleCase.customerId,
        verify
          ? undefined
          : changed
            ? {
                type: BankAccountType.Danish,
                registrationNumber: accountNumber[0],
                accountNumber: accountNumber[1],
              }
            : undefined,
      )
      .then(async () => {
        setEditingBankAccount(false)

        q.invalidate(keys.write.modify(customersEntity, singleCase!.customerId))
        await ensureCustomerLoaded(singleCase!.customerId)
        setAccountNumber(['', ''])
      })
  }

  if (!singleCase) {
    return (
      <Content fullHeight>
        <Container>
          <Row>
            <Col size="md" width="5" style={{ paddingTop: '80px' }}>
              <PlaceholderLoader style={{ marginTop: '30px' }} />
              <PlaceholderLoader width="40%" style={{ marginTop: '30px' }} />
              <PlaceholderLoader width="60%" style={{ marginTop: '30px' }} />
            </Col>
          </Row>
        </Container>
      </Content>
    )
  }

  if (caseError) {
    return (
      <PageBase>
        <Panel>{caseError.message}</Panel>
      </PageBase>
    )
  }

  if (singleCase && singleCase.deleted) {
    return (
      <PageBase>
        <Panel>{lang.cases.caseHasBeenDeleted}</Panel>
      </PageBase>
    )
  }

  const paid =
    props.paid ||
    (caseVouchers && -helpers.overview.balance(caseVouchers) < 500)

  const hasPayment =
    caseVouchers &&
    caseVouchers.find(
      (cv) => cv.category === VoucherCategory.TRANSACTION && cv.amount > 0,
    )

  const overpayment = caseVouchers && helpers.overview.balance(caseVouchers) > 0

  const bankAccountExists = customer?.bankAccount != null

  const bankAccountNeedsVerification =
    h.hasData(pendingOverpayments) && pendingOverpayments.data.length > 0

  return (
    <div>
      <Content fullHeight>
        <Container>
          <Row>
            <Col size="md" width="12">
              {paid && (
                <Panel style={{ background: 'white', marginTop: '50px' }}>
                  <h1 style={{ textAlign: 'center' }}>
                    {hasPayment
                      ? lang.debtorWeb.congratsDeposit
                      : lang.debtorWeb.congratsCaseEnd}{' '}
                    <ThumbsUp />
                  </h1>
                  <p style={{ textAlign: 'center' }}>
                    {lang.debtorWeb.satisfactionOne}{' '}
                    {
                      // @ts-ignore
                      <Link to={`/cases/${singleCase.id}/satisfaction`}>
                        {lang.debtorWeb.satisfactionTwo}
                      </Link>
                    }{' '}
                    {lang.debtorWeb.satisfactionThree}
                  </p>
                </Panel>
              )}
            </Col>
          </Row>
          <Row>
            {overpayment && (
              <Col size="md" width="12">
                <Panel style={{ textAlign: 'center' }}>
                  <h1>{lang.debtorWeb.overpaymentHeader}</h1>
                  <p>
                    {lang.debtorWeb.overpaymentPreAmount} {singleCase.currency}{' '}
                    <Money amount={helpers.overview.balance(caseVouchers)} />{' '}
                    {lang.debtorWeb.overpaymentPostAmount}
                  </p>
                  {bankAccountExists && !editingBankAccount ? (
                    <>
                      <BankAccount
                        editable={true}
                        onEdit={() => setEditingBankAccount(true)}
                        accountDetails={customer!.bankAccount!}
                      />
                      {bankAccountNeedsVerification && (
                        <>
                          <br />
                          <Button
                            onClick={(e) => {
                              onSubmit(e, true)
                            }}
                          >
                            {lang.misc.submit}
                          </Button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <form
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onSubmit={onSubmit}
                      >
                        <AccountInput
                          onChange={(accountNumber) =>
                            setAccountNumber(accountNumber)
                          }
                          value={accountNumber}
                        />
                        <Button
                          style={{ marginBottom: '20px', padding: '13px' }}
                        >
                          {lang.misc.submit}
                        </Button>
                      </form>
                      {editingBankAccount && (
                        <Button
                          theme="secondary"
                          size="small"
                          onClick={() => setEditingBankAccount(false)}
                        >
                          {lang.misc.cancel}
                        </Button>
                      )}
                    </>
                  )}
                </Panel>
              </Col>
            )}
          </Row>
          <Row>
            <Col size="md" width="6">
              {singleCase && (
                <Intro>
                  {profileData && (
                    <h1 style={{ marginTop: '80px' }}>
                      {lang.debtorWebHome.dear} <span>{profileData.name}</span>
                    </h1>
                  )}
                  {meRole && meRole === PermissionLevels.alternativeContact && (
                    <p>{lang.debtorWebHome.representative}</p>
                  )}
                  {tenant?.settings?.debtorweb?.welcomeText ? (
                    <p>{tenant?.settings?.debtorweb?.welcomeText}</p>
                  ) : (
                    <p>
                      {meRole && meRole === PermissionLevels.alternativeContact
                        ? lang.debtorWebHome.debtor
                        : lang.debtorWebHome.you}{' '}
                      {lang.debtorWebHome.owe}{' '}
                      <b style={{ color: 'black' }}>
                        {singleCase && singleCase.currency}{' '}
                        {caseVouchers && (
                          <Money
                            amount={-helpers.overview.balance(caseVouchers)}
                          />
                        )}
                      </b>{' '}
                      {lang.debtorWebHome.to}{' '}
                      <b style={{ color: 'black' }}>
                        {creditor && creditor.name}
                      </b>
                      . {lang.debtorWebHome.youCanDoThisHere}
                    </p>
                  )}
                </Intro>
              )}
              <OnlyMobile style={{ margin: `${THEME.GRID_SPACING}px 0` }}>
                {collector && (
                  <Pay
                    case={singleCase}
                    collector={collector}
                    isPaymentPlan={false}
                  />
                )}
              </OnlyMobile>
              {caseVouchers && (
                <CaseVouchers
                  hideExpiredCaseVouchers
                  // Debtors see what they owe, not vouchers written off
                  // whole.
                  caseVouchers={caseVouchers
                    .map((cv) => ({
                      ...cv,
                      children: [],
                    }))
                    .filter(helpers.vouchers.isListed)}
                  singleCase={singleCase}
                />
              )}
              <Header>{lang.debtorWebHome.yourInfo}</Header>
              <DebtorInfo caseId={singleCase.caseId} />
              {creditor && creditor.debtorFAQ && (
                <>
                  <Header>{lang.debtorWebHome.faq}</Header>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: creditor.debtorFAQ,
                    }}
                  ></div>
                </>
              )}
              {documents && !!documents.length && (
                <>
                  <Header>{lang.debtorWeb.documentation}</Header>
                  <div
                    style={{
                      overflowX: 'scroll',
                      overflowY: 'hidden',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                    }}
                  >
                    {documents.map((document) => (
                      <Documentation key={document.id} document={document} />
                    ))}
                  </div>
                </>
              )}
              {!tenant?.settings?.debtorweb?.hideContactInformation && (
                <>
                  <Header>{lang.debtorWebHome.contactUs}</Header>
                  {collector && (
                    <Row>
                      <Col size="md" width="8">
                        <p>
                          {tenant?.settings?.debtorweb?.contactUsText ||
                            lang.debtorWebHome.contactUsText}
                        </p>
                        <Info>
                          {collector.email && (
                            <>
                              <Mail /> {collector.email}
                            </>
                          )}
                          {validatePhone(collector.phone) && (
                            <>
                              <Phone
                                style={{
                                  marginLeft: collector.email
                                    ? `${THEME.GRID_SPACING}px`
                                    : '0px',
                                }}
                              />{' '}
                              +{collector.phone.locale} {collector.phone.number}
                            </>
                          )}
                        </Info>
                      </Col>
                      <Col size="md" width="4">
                        {collector.logo ? (
                          <img
                            width="100%"
                            alt={collector.logo.fileName}
                            src={`${APIBASE}/files/${collector.logo.fileId}/${collector.logo.fileName}`}
                          />
                        ) : (
                          <span>{collector.name}</span>
                        )}
                      </Col>
                    </Row>
                  )}
                  <br />
                  <br />
                  {singleCase && (
                    <NoteInteraction
                      referenceId={singleCase.caseId}
                      referenceType={Entity.Case}
                      id={singleCase.id}
                      options={{
                        direction: Direction.INCOMING,
                        type: InteractionType.DebtorNote,
                      }}
                      sendButtonText={lang.debtorWebHome.send}
                      textAreaPlaceholder={lang.debtorWebHome.yourMessage}
                      editorMinHeight="80px"
                      disableSend={needsEmail && !validateEmail(draftEmail)}
                      onBeforeSend={saveDebtorEmailIfNeeded}
                      extraFields={
                        needsEmail && (
                          <div
                            style={{ marginBottom: `${THEME.GRID_SPACING}px` }}
                          >
                            <div
                              style={{
                                marginBottom: '6px',
                                fontWeight: 600,
                              }}
                            >
                              {lang.debtorWebHome.enterEmail}
                            </div>
                            <Input
                              required
                              type="email"
                              placeholder={lang.debtorWebHome.email}
                              value={draftEmail}
                              onChange={(e) => {
                                setDraftEmail(e.target.value)
                                setEmailErrors(null)
                              }}
                              validationErrors={emailErrors}
                            />
                          </div>
                        )
                      }
                    />
                  )}
                  {id && <CaseHistory caseId={id} />}
                </>
              )}
            </Col>
            <Col size="md" width="1" />
            <Col
              size="md"
              width="5"
              style={{
                position: 'sticky',
                top: `${THEME.GRID_SPACING}px`,
                marginTop: '50px',
              }}
            >
              <OnlyNotMobile>
                <Pay
                  case={singleCase}
                  collector={collector}
                  isPaymentPlan={false}
                />
              </OnlyNotMobile>
            </Col>
          </Row>
        </Container>
        <Footer>
          {collector && (
            <ul>
              <li>
                <a onClick={() => c.auth.logout()}>
                  {lang.debtorWebHome.logout}
                </a>
              </li>
              {collector.privacyPolicy && (
                <li>
                  <a target="_blanc" href={collector.privacyPolicy}>
                    {lang.debtorWebHome.privacyPolicy}
                  </a>
                </li>
              )}
              {collector.businessTerms && (
                <li>
                  <a target="_blanc" href={collector.businessTerms}>
                    {lang.debtorWebHome.businessTerms}
                  </a>
                </li>
              )}
              <li>{collector.name}</li>
              {collector.centralRegisterId && (
                <li>CVR: {collector.centralRegisterId}</li>
              )}
              {collector.address &&
                collector.address.address &&
                collector.address.zipcode &&
                collector.address.city && (
                  <li>
                    {collector.address.address}, {collector.address.zipcode}{' '}
                    {collector.address.city}
                  </li>
                )}
            </ul>
          )}
          <div
            style={{
              textAlign: 'center',
              marginTop: '2em',
            }}
          >
            <LanguageInput
              language={currentLang}
              onChange={(lang) => c.lang.setLang(lang)}
              availableLanguages={[Lang.da, Lang.en]}
            />
          </div>
          {collector && collector.logo && (
            <img
              alt={collector.logo.fileName}
              src={`${APIBASE}/files/${collector.logo.fileId}/${collector.logo.fileName}`}
            />
          )}
        </Footer>
      </Content>
    </div>
  )
}

export default Case
